/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';
import type { DataValue } from 'react-apollo';
import { FormattedHTMLMessage } from 'react-intl';

import { difference, uniqBy } from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Checkbox, CheckboxGroup, Dialog, InlineNotification, LoadingSection, TextField } from '@coursera/cds-core';

import type {
  FindByOrganizationIdAndIncludeDeletedRequest,
  FindByOrganizationIdAndIncludeDeletedResponse,
  FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedRequest,
  FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedResponse,
} from 'bundles/enterprise-admin-home/queries/AdminHomeRostersQuery';
import adminHomeRostersQuery from 'bundles/enterprise-admin-home/queries/AdminHomeRostersQuery';
import linkContractsToProgramMutation from 'bundles/enterprise-admin-home/queries/LinkContractsToProgramMutation';
import type {
  LinkContractsToProgramRequest,
  LinkContractsToProgramResponse,
} from 'bundles/enterprise-admin-home/queries/LinkContractsToProgramMutation';
// GQL
import programSummaryContractsModalQuery from 'bundles/enterprise-admin-home/queries/ProgramSummaryContractsModal.graphql';
import type {
  ProgramSummaryContractsModalQuery,
  ProgramSummaryContractsModalQueryVariables,
} from 'bundles/enterprise-admin-home/queries/__generated__/ProgramSummaryContractsModal';
import { Button, Spacer } from 'bundles/enterprise-ui/components';

// i18n
import _t from 'i18n!nls/enterprise-admin-home';

type PropsFromCaller = {
  thirdPartyOrganizationId: string;
  programId: string;
  handleClose: () => void;
};

type PropsFromGraphql1 = {
  loadingRosterQuery: boolean;
  defaultSelectedContractIds: string[];
  availableContractIds?: string[];
};

type PropsFromGraphql2 = {
  data: DataValue<ProgramSummaryContractsModalQuery, ProgramSummaryContractsModalQueryVariables>;
};

type PropsFromGraphql3 = {
  linkContractsToProgram: (request: {
    variables: {
      input: LinkContractsToProgramRequest;
    };
  }) => Promise<{
    data: {
      linkContractsToProgram: LinkContractsToProgramResponse;
    };
  }>;
};

export type Props = PropsFromCaller & PropsFromGraphql1 & PropsFromGraphql2 & PropsFromGraphql3;

type State = {
  newSelectedIds: string[];
  contractSearch: string;
  contractsFailed?: string[];
};

export class ProgramSummaryContractsModal extends React.Component<Props, State> {
  static contextTypes = {
    addToast: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof ProgramSummaryContractsModal.contextTypes>;

  constructor(props: Props) {
    super(props);

    this.state = {
      newSelectedIds: [],
      contractSearch: '',
    };
  }

  handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      contractSearch: e.target.value,
    });
  };

  /**
   * We only support toggling contracts *ON*
   *
   * Turning them off was an initial request but was pushed back on because
   * of technical limitations.
   */
  handleToggle = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const { defaultSelectedContractIds } = this.props;
    const { newSelectedIds } = this.state;
    const idToToggle = event.target.value;

    const fullSelectedIds = checked ? [...newSelectedIds, idToToggle] : newSelectedIds.filter((_) => _ !== idToToggle);
    const additionalIds = difference(fullSelectedIds, defaultSelectedContractIds);

    this.setState({
      newSelectedIds: additionalIds,
    });
  };

  handleDeselect = () => {
    this.setState({ newSelectedIds: [] });
  };

  handleSave = async () => {
    const { handleClose, programId, linkContractsToProgram } = this.props;
    const { newSelectedIds } = this.state;
    const { addToast } = this.context;

    if (!newSelectedIds.length) {
      handleClose();
      return;
    }
    try {
      this.setState({ contractsFailed: undefined });
      const response = await linkContractsToProgram({
        variables: {
          input: {
            programId,
            contractIds: newSelectedIds,
          },
        },
      });
      const contractsFailed = (response?.data as $TSFixMe)?.LinkContractsToProgramMutation?.contractsFailed;
      if (contractsFailed?.length > 0) {
        this.setState({ contractsFailed });
      } else {
        handleClose();
      }
    } catch {
      addToast(_t('Something went wrong!'));
    }
  };

  render() {
    const { handleClose, data, loadingRosterQuery, defaultSelectedContractIds } = this.props;
    const { newSelectedIds, contractSearch, contractsFailed } = this.state;

    const availableContracts = data?.EnterpriseContract?.queryContractByIds;

    // This is to filter out duplicates, garbage data.
    const uniqAvailableContracts = uniqBy(availableContracts, (val) => val?.id);

    const searchExcluded = (val: { id: string; name?: string | null }): boolean => {
      if (contractSearch) {
        const nameFound = val.name?.toLowerCase().includes(contractSearch.toLowerCase());
        const idFound = val.id === contractSearch;

        return !nameFound && !idFound;
      }

      return false;
    };

    type CheckListOption = {
      id: string;
      label?: string | null;
      disabled: boolean;
      searchExcluded: boolean;
    };
    const checkListOptions: CheckListOption[] = uniqAvailableContracts.reduce((options, val) => {
      if (!val) return options;
      const disabled = defaultSelectedContractIds.includes(val.id);
      return [
        ...options,
        {
          id: val.id,
          label: val.name,
          disabled,
          searchExcluded: searchExcluded(val),
        },
      ];
    }, [] as CheckListOption[]);

    const content = (
      <div>
        <div>
          <TextField
            name="ContractSearch"
            label={_t('Search')}
            placeholder={_t('Start typing the Contract Name to search...')}
            value={contractSearch}
            onChange={this.handleSearch}
            fullWidth
          />
          <div
            css={{
              position: 'relative',
              height: '300px',
              overflowY: 'auto',
              marginTop: 16,
            }}
          >
            {(loadingRosterQuery || data?.loading) && <LoadingSection />}

            <CheckboxGroup
              label={_t('Contracts')}
              value={defaultSelectedContractIds.concat(newSelectedIds)}
              onChange={this.handleToggle}
            >
              {checkListOptions
                .filter((_) => !_.searchExcluded)
                .map((val) => {
                  return (
                    <div key={val.id}>
                      <Checkbox value={val.id} name={val.id} disabled={val.disabled} label={val.label || val.id} />
                    </div>
                  );
                })}
            </CheckboxGroup>
          </div>
        </div>
      </div>
    );

    return (
      <Dialog className="rc-ProgramSummaryContractsModal" open onClose={handleClose}>
        <Dialog.HeadingGroup supportText={_t('Choose the contracts you want this program to be used in.')}>
          {_t('Add contracts')}
        </Dialog.HeadingGroup>
        <Dialog.Content>
          {content}
          {contractsFailed && (
            <React.Fragment>
              <Spacer size={24} />
              <InlineNotification severity="error" title={_t('Error!')}>
                <FormattedHTMLMessage
                  message={_t(
                    'Failed to link contracts: {contracts}. Please check that the contracts are compatible with existing contracts and check out {link} if you need to link incompatible contracts.'
                  )}
                  contracts={contractsFailed
                    .map((id) => checkListOptions.find((option) => option.id === id)?.label ?? id)
                    .join(', ')}
                  link={
                    <a
                      href="https://coursera.atlassian.net/wiki/spaces/ENT/pages/2503901279/Resolving+program+contract+linking+errors"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {_t('the documentation')}
                    </a>
                  }
                />
              </InlineNotification>
            </React.Fragment>
          )}
        </Dialog.Content>
        <Dialog.Actions>
          <Button variant="secondary" label={_t('Deselect All')} onClick={this.handleDeselect} />
          <Button css={{ marginLeft: 24 }} variant="primary" label={_t('OK')} onClick={this.handleSave} />
        </Dialog.Actions>
      </Dialog>
    );
  }
}

export const rostersMapper = ({
  data,
}: {
  data: {
    loading: boolean;
    byProgram: FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedResponse;
    byOrg: FindByOrganizationIdAndIncludeDeletedResponse;
  };
}) => {
  return {
    loadingRosterQuery: data.loading,
    defaultSelectedContractIds: data.byProgram?.rosterRecords.map((record) => record.contractId) ?? [],
    availableContractIds: data.byOrg?.rosterRecords.map((record) => record.contractId),
  };
};

const enhancedProgramSummaryContractsModal = compose<Props, PropsFromCaller>(
  graphql(adminHomeRostersQuery, {
    options: ({ thirdPartyOrganizationId, programId }: PropsFromCaller) => {
      const byProgramInput: FindByProgramIdsOrContractIdsOrBothAndIncludeDeletedRequest = {
        programIds: [programId],
        contractIds: [],
      };

      const byOrgInput: FindByOrganizationIdAndIncludeDeletedRequest = {
        organizationId: thirdPartyOrganizationId,
      };

      return {
        variables: {
          byProgramInput,
          byOrgInput,
        },
        fetchPolicy: 'network-only', // Always fetch the most recent contract connections
        errorPolicy: 'all',
      };
    },
    props: rostersMapper as $TSFixMe,
  }),
  graphql<
    PropsFromGraphql1,
    ProgramSummaryContractsModalQuery,
    ProgramSummaryContractsModalQueryVariables,
    PropsFromGraphql2
  >(programSummaryContractsModalQuery, {
    skip: (props) => !props.availableContractIds,
    options: ({ availableContractIds }) => {
      return {
        variables: {
          // ^ skipping if falsy
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          ids: availableContractIds!,
        },
        fetchPolicy: 'network-only', // Always fetch the most recent contract connections
        context: { clientName: 'gatewayGql' },
      };
    },
  }),
  graphql(linkContractsToProgramMutation, {
    name: 'linkContractsToProgram',
  })
);

export default enhancedProgramSummaryContractsModal(ProgramSummaryContractsModal);
